import React, { useEffect, useState } from 'react';
import { Form } from '@progress/kendo-react-form';
import { useToast } from 'shared/toast';
import useIncrement from 'shared/grids/hooks/useIncrement';
import * as yup from 'yup';
import isNil from 'lodash/isNil';

// eslint-disable-next-line no-unused-vars
const { BaseFormProps, BaseFormRenderProps } = require('./typedefs');

const typeMapper = {
  [null]: yup.mixed(),
  mixed: yup.mixed(),
  date: yup.date(),
  string: yup.string(),
  number: yup.number(),
  array: yup.array(),
};

/**
 * @typedef {Object} AdditionalBaseFormRenderProps
 * @property {React.Context<any>} Context
 */

export const BaseFormRenderPropsContext = React.createContext({});

/**
 * @returns {BaseFormRenderProps & AdditionalBaseFormRenderProps}
 */
export const useBaseFormRenderProps = () => React.useContext(BaseFormRenderPropsContext);

/**
 * @param {BaseFormProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const BaseForm = ({
  isSubmitting: isParentSubmitting,
  render,
  onSubmit,
  onSubmitClick,
  onError,
  // validation with Yup
  initialValidationSchema,
  validationSchema,
  setValidationSchema,
  validationContext,
  resetFieldValidation,
  // Use for nested forms so that context is not over written
  getContext,
  ...props
}, ref) => {
  const toast = useToast();

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isNil(isParentSubmitting)) {
      setIsSubmitting(isParentSubmitting);
    }
  }, [isParentSubmitting]);

  const [submitCount, incrementSubmitCount] = useIncrement();

  const OwnContext = React.useMemo(() => React.createContext({}), []);

  const renderForm = React.useCallback(
    (rProps) => {
      const mappedProps = {
        ...rProps,
        isSubmitting,
        submitCount,
      };
      /* NOTE: Be careful about manually using onFailed with resetFieldValidation
      When you set some errors manualy for a field the below logic may not work as you expected!
      onChange is for emiting new changes not for tracking changes on value.
      If you use onFailed with resetFieldValidation to set some manual errors dynamically,
      you must set the component value using this onChange on <Field> to
      reset it's validation state otherwise component validation will not be resetted
      and submit will not be triggered */
      if (resetFieldValidation) {
        mappedProps.onChange = (fieldName, valueObj) => {
          resetFieldValidation(fieldName);
          rProps.onChange?.(fieldName, valueObj);
        };
      }
      // Return custom context for access when a child form could over write
      getContext?.(OwnContext);
      mappedProps.Context = OwnContext;
      return <OwnContext.Provider value={mappedProps}><BaseFormRenderPropsContext.Provider value={mappedProps}>{render(mappedProps)}</BaseFormRenderPropsContext.Provider></OwnContext.Provider>;
    },
    [render, isSubmitting, submitCount],
  );

  const handleValidation = React.useCallback(
    (values) => {
      if (!validationSchema) {
        return;
      }

      try {
        validationSchema.validateSync(values, {
          abortEarly: false,
          context: validationContext,
        });
      } catch (error) {
        const validationErrors = error?.inner
          ?.map(({ path, message }) => ({ [path]: message }))
          ?.reduce((acc, curr) => {
            const key = Object.keys(curr)[0];
            return { ...acc, [key]: curr[key] };
          }) ?? [error.message];

        console.warn('Validation errors:', validationErrors);

        return {
          // Set first validation error as a summary
          VALIDATION_SUMMARY: error.inner?.[0]?.message ?? error.message,
          ...validationErrors,
        };
      }
    },
    [validationSchema],
  );

  // Returns new yup validation schemas with validation errors from server, if any
  const handleValidationErrors = (errors) => {
    if (errors) {
      const schemas = [];
      Object.keys(errors).map((field) => {
        errors[field].forEach((error) => {
          const fieldName = field.charAt(0).toLowerCase() + field.slice(1);

          const typeName = initialValidationSchema ? yup.reach(initialValidationSchema, fieldName)?._type : null;

          const type = typeMapper[typeName];
          if (type) {
            const newSchema = yup.object().shape({
              [fieldName]: type.nullable().test(
                'required',
                error,
                () => false, // force error
              ),
            });
            schemas.push(newSchema);
          }
        });
      });
      return schemas;
    }
    return null;
  };

  const handleClickSubmit = React.useCallback(() => {
    incrementSubmitCount();
    if (onSubmitClick) {
      onSubmitClick();
    }
  }, []);

  const handleSubmit = React.useCallback(
    (values, event) => {
      if (validationSchema) {
        try {
          validationSchema.validateSync(values, {
            abortEarly: false,
          });
        } catch (error) {
          toast.error({ error, message: 'There are validation errors' });
          return;
        }
      }

      setIsSubmitting(true);

      const onFinish = () => setIsSubmitting(false);

      // Update validation schema if received one
      const onFailed = (error, convertValidationErrors) => {
        const validationErrors = error?.response?.data?.errors;

        if (validationErrors) {
          const schemas = handleValidationErrors(
            convertValidationErrors ? convertValidationErrors(validationErrors) : validationErrors,
          );

          // Show first validation error
          const firstField = Object.keys(validationErrors)[0];
          const fieldError = validationErrors[firstField][0];

          if (fieldError) {
            toast.error({ message: fieldError });
          }

          // Update validation schema
          if (schemas) {
            let currSchema = initialValidationSchema;
            schemas.forEach((schema) => {
              currSchema = currSchema ? currSchema.concat(schema) : schema;
            });

            if (setValidationSchema) {
              setValidationSchema(currSchema);
            }
          }
        } else {
          toast.error({
            message:
              error.response?.data?.Detail
              ?? error.response?.data?.Description
              ?? error.response?.StatusText
              ?? error.message,
          });
        }
      };

      return onSubmit(values, null, onFailed, onFinish, event);
    },
    [validationSchema, initialValidationSchema, onSubmit],
  );

  return (
    <Form
      render={renderForm}
      onSubmit={handleSubmit}
      onSubmitClick={handleClickSubmit}
      validator={handleValidation}
      ref={ref}
      {...props}
    />
  );
};

/**
 *  A base form with various options and handlers.
 *
 * @param {BaseFormProps} props Component props. Extends Telerik Form props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Base form component with various options and handlers.
 */
const ForwardForm = React.forwardRef(BaseForm);

export default ForwardForm;
